/**
 * Blessed Commerce theme behaviour.
 * Custom elements upgrade on their own, so this also works when the theme
 * editor re-renders a section.
 */

/**
 * <bc-menu>: full-screen mobile menu built on a native <dialog>.
 * The dialog gives focus trapping and an inert page for free. State is reset
 * directly on close rather than in the dialog's `close` event, which some
 * browsers fire late, so a tapped anchor link can scroll straight away.
 */
class BcMenu extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.openButton = this.querySelector('[data-bc-menu-open]');
    if (!this.dialog || !this.openButton) return;

    this.onOpen = () => this.open();
    this.onDialogClick = (event) => {
      if (event.target.closest('[data-bc-menu-close]') || event.target.closest('a[href]')) this.close();
    };
    this.onKeydown = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      this.close();
      this.openButton.focus();
    };
    this.onDialogClose = () => this.reset();
    this.desktopQuery = window.matchMedia('(min-width: 900px)');
    this.onBreakpoint = (event) => {
      if (event.matches) this.close();
    };

    this.openButton.addEventListener('click', this.onOpen);
    this.dialog.addEventListener('click', this.onDialogClick);
    this.dialog.addEventListener('keydown', this.onKeydown);
    this.dialog.addEventListener('close', this.onDialogClose);
    this.desktopQuery.addEventListener('change', this.onBreakpoint);
  }

  disconnectedCallback() {
    if (!this.dialog || !this.openButton) return;
    this.openButton.removeEventListener('click', this.onOpen);
    this.dialog.removeEventListener('click', this.onDialogClick);
    this.dialog.removeEventListener('keydown', this.onKeydown);
    this.dialog.removeEventListener('close', this.onDialogClose);
    this.desktopQuery.removeEventListener('change', this.onBreakpoint);
    this.reset();
  }

  open() {
    if (this.dialog.open) return;
    this.dialog.showModal();
    this.openButton.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('bc-scroll-lock');
  }

  close() {
    if (this.dialog.open) this.dialog.close();
    this.reset();
  }

  reset() {
    this.openButton.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('bc-scroll-lock');
  }
}

if (!customElements.get('bc-menu')) customElements.define('bc-menu', BcMenu);

/**
 * <bc-process>: marks the step being read (its numeral fills in and the index
 * link lights up) and lets steps rise in as they reach the screen. Without
 * IntersectionObserver, or in the theme editor, every step simply shows.
 */
class BcProcess extends HTMLElement {
  connectedCallback() {
    this.steps = [...this.querySelectorAll('[data-step]')];
    this.links = [...this.querySelectorAll('[data-step-link]')];
    if (!this.steps.length || !('IntersectionObserver' in window)) return;

    this.tracker = new IntersectionObserver(
      (entries) => entries.forEach((entry) => entry.isIntersecting && this.setActive(entry.target.dataset.step)),
      { rootMargin: '-45% 0px -50% 0px' }
    );
    this.steps.forEach((step) => this.tracker.observe(step));
    this.setActive(this.steps[0].dataset.step);

    if (window.Shopify?.designMode) return;
    this.revealer = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          this.revealer.unobserve(entry.target);
        }),
      { rootMargin: '0px 0px -8% 0px', threshold: 0.1 }
    );
    this.classList.add('is-enhanced');
    this.steps.forEach((step) => this.revealer.observe(step));
  }

  disconnectedCallback() {
    this.tracker?.disconnect();
    this.revealer?.disconnect();
  }

  setActive(index) {
    this.steps.forEach((step) => step.classList.toggle('is-active', step.dataset.step === index));
    this.links.forEach((link) => {
      if (link.dataset.stepLink === index) link.setAttribute('aria-current', 'step');
      else link.removeAttribute('aria-current');
    });
  }
}

if (!customElements.get('bc-process')) customElements.define('bc-process', BcProcess);

/**
 * <bc-reveal>: children marked [data-reveal] rise in as they reach the screen.
 * The hidden starting state only applies once this runs, and never in the
 * theme editor, so content is always there without it.
 */
class BcReveal extends HTMLElement {
  connectedCallback() {
    if (window.Shopify?.designMode || !('IntersectionObserver' in window)) return;
    const items = [...this.querySelectorAll('[data-reveal]')];
    if (!items.length) return;

    this.observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          this.observer.unobserve(entry.target);
        }),
      { rootMargin: '0px 0px -8% 0px', threshold: 0.1 }
    );
    this.classList.add('bc-reveal-ready');
    items.forEach((item) => this.observer.observe(item));
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

if (!customElements.get('bc-reveal')) customElements.define('bc-reveal', BcReveal);
